/**
 * users.repository.ts — 사용자 목록(관리용) 구독.
 *
 * Firestore: users/{uid} — { login_id, name, email, phone, role, active, created_at, ... }
 *   role/active의 source of truth는 custom claims이나, 다른 사용자 claims는 클라에서 못 읽으므로
 *   백엔드(processAdminOp)가 users 문서에 role/active를 미러한다. 목록은 이 미러를 읽는다.
 * rules: read = isAdmin || isSelf || (가입 대기자 ∧ 신청 법인의 법인 관리자). 관리 목록(watch)은 admin 전제,
 *   법인 관리자는 watchPendingSignups(rules 와 같은 where)만.
 */
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where, type DocumentData } from "firebase/firestore";
import { db } from "./firebase";
import { runAdminOp } from "./admin_ops.service";
import { isTenantKey, type TenantKey } from "@/domain/space";
import type { DeviceDoc } from "@/domain/session";
import { deviceFromFirestore } from "./devices.repository";
import { unitRolesObject, type Employment, type MemberUnit } from "@/domain/membership";

export interface AdminUserRow {
  uid: string;
  loginId: string;
  name: string;
  /** 역할 id (roles 문서 id). null = 미승인(역할 없음) */
  role: string | null;
  active: boolean;
  email: string;
  /** 연락처 */
  phone: string;
  /** 업무용 이메일 (선택) */
  workEmail: string;
  /** 개인 이메일 (가입 시 필수) */
  personalEmail: string;
  /** 생년월일 (YYYY-MM-DD) */
  birthDate: string;
  /** 통합 직급(사람당 1개, 두 법인 공통 — domain/rank.ts). 빈 값 = 미지정 */
  rank: string;
  createdAt: string;
  /** 근무 법인 목록(users.tenants — 함수가 arrayUnion/Remove). 12 설계 §4-2 */
  tenants: TenantKey[];
  /** 활성 기기 수(함수 onDeviceWrite 가 씀). 아직 기록 전이면 null */
  deviceCount: number | null;
  /** 마지막 접속(함수 onAccessLogCreate 가 씀). ISO, 없으면 "" */
  lastLoginAt: string;
  /** 가입 화면에서 고른 근무 법인(users.signup_tenant). 없으면 null(법인을 고르지 않은 옛 신청·관리자가 만든 계정) */
  signupTenant: TenantKey | null;
  /** 삭제된 계정(users.deleted — 함수 tombstoneUserAccount). Auth·기기는 지워졌고 이름·아이디만 기록으로 남는다 */
  deleted: boolean;
  /** 삭제 일시(ISO). 삭제되지 않았으면 "" */
  deletedAt: string;
  /** 삭제한 관리자 이름 */
  deletedByName: string;
}

function rowFromDoc(uid: string, x: DocumentData): AdminUserRow {
  return {
    uid,
    loginId: typeof x.login_id === "string" ? x.login_id : "",
    name: typeof x.name === "string" ? x.name : "",
    role: typeof x.role === "string" && x.role ? x.role : null,
    active: x.active !== false,
    email: typeof x.email === "string" ? x.email : "",
    phone: typeof x.phone === "string" ? x.phone : "",
    workEmail: typeof x.work_email === "string" ? x.work_email : "",
    personalEmail: typeof x.personal_email === "string" ? x.personal_email : "",
    birthDate: typeof x.birth_date === "string" ? x.birth_date : "",
    rank: typeof x.rank === "string" ? x.rank : "",
    createdAt: tsToIso(x.created_at),
    tenants: Array.isArray(x.tenants) ? x.tenants.filter(isTenantKey) : [],
    deviceCount: typeof x.device_count === "number" ? x.device_count : null,
    lastLoginAt: tsToIso(x.last_login_at),
    signupTenant: isTenantKey(x.signup_tenant) ? x.signup_tenant : null,
    deleted: x.deleted === true,
    deletedAt: tsToIso(x.deleted_at),
    deletedByName: typeof x.deleted_by_name === "string" ? x.deleted_by_name : "",
  };
}

/** claims와 무관한 프로필 필드 — admin이 users 문서에 직접 기록. */
export interface UserProfilePatch {
  name?: string;
  phone?: string;
  work_email?: string;
  personal_email?: string;
  birth_date?: string;
  /** 통합 직급 — 소속 문서 사본은 함수(onUserNameChange)가 전파 */
  rank?: string;
}

function tsToIso(v: unknown): string {
  if (v && typeof (v as { toDate?: () => Date }).toDate === "function") {
    return (v as { toDate: () => Date }).toDate().toISOString();
  }
  return typeof v === "string" ? v : "";
}

export const usersRepo = {
  watch(cb: (rows: AdminUserRow[]) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      collection(db, "users"),
      (snap) => {
        const rows = snap.docs.map((d) => rowFromDoc(d.id, d.data()));
        // 활성 먼저, 삭제된 계정은 맨 뒤, 그 다음 이름
        rows.sort((a, b) => {
          if (a.deleted !== b.deleted) return a.deleted ? 1 : -1;
          if (a.active !== b.active) return a.active ? -1 : 1;
          return a.name.localeCompare(b.name, "ko");
        });
        cb(rows);
      },
      onError,
    );
  },

  /**
   * 한 법인으로 들어온 가입 신청(가입 대기) — 법인 관리자용(법인 환경설정 › 사용자 관리).
   * rules users read 의 pendingSignupReadable 과 같은 where(signup_tenant == t ∧ active == false ∧ role == "")라야 통과한다.
   * 신청 순서(오래된 것 먼저)로 정렬.
   */
  watchPendingSignups(tenant: TenantKey, cb: (rows: AdminUserRow[]) => void, onError?: (e: Error) => void): () => void {
    const q = query(
      collection(db, "users"),
      where("signup_tenant", "==", tenant),
      where("active", "==", false),
      where("role", "==", ""),
    );
    return onSnapshot(
      q,
      (snap) => cb(snap.docs.map((d) => rowFromDoc(d.id, d.data())).sort((a, b) => a.createdAt.localeCompare(b.createdAt))),
      onError,
    );
  },

  /**
   * 한 사람의 기기 목록(users/{uid}/devices) — 통합관리 B-4. rules: read = isAdmin || 본인.
   * 해제된 기기(status revoked)도 함께 돌려준다(화면이 거른다). 최근 접속 순.
   */
  watchDevices(uid: string, cb: (rows: DeviceDoc[]) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      collection(db, "users", uid, "devices"),
      (snap) => {
        const rows = snap.docs.map((d) => deviceFromFirestore(d.id, d.data()));
        rows.sort((a, b) => (b.last_seen_at?.getTime() ?? 0) - (a.last_seen_at?.getTime() ?? 0));
        cb(rows);
      },
      onError,
    );
  },

  /** 프로필(이름·연락처·이메일·생년월일 등) 직접 수정 — claims와 무관한 필드. rules: admin write 허용. */
  async updateProfile(uid: string, patch: UserProfilePatch): Promise<void> {
    await updateDoc(doc(db, "users", uid), { ...patch, updated_at: serverTimestamp() });
  },
};

// ── 계정·기기·세션 요청 (admin_ops) — 12 설계 §6-1 ──────────────────────────────
// 옛 action(user.deactivate·user.delete·user.reset_pw)은 현행 파라미터 이름(targetUid·id)을 그대로 쓴다.
// 새 action 은 uid 로 통일(guard 가 params.uid|targetUid|id 를 모두 읽는다 — 설계 §6-1 ③).

export interface CreateWithMembershipInput {
  loginId: string;
  name: string;
  password: string;
  tenant: TenantKey;
  /** 사업부별 부서(2026-10-26) — 함수가 role(권한용)·bizs 파생 */
  units: MemberUnit[];
  employment: Employment;
  /** 소속 시작일 YYYY-MM-DD */
  startDate: string;
  /** 통합 직급(선택) — 함수가 목록(system_settings/rank_list) 안의 값인지 검사 */
  rank?: string;
  /** 이메일(선택) — 함수가 users.personal_email 에 씀(가입 화면과 같은 값). 업무 이메일은 받지 않음 */
  personalEmail?: string;
}

/** 가입 승인 — 계정 추가와 같은 소속 칸(법인·사업부별 부서·근무 형태·시작일·직급) */
export interface ApproveSignupInput {
  uid: string;
  /** 승인할 근무 법인 — 법인 관리자는 신청 법인(=자기 법인)만, 전체 admin 은 바꿀 수 있음 */
  tenant: TenantKey;
  units: MemberUnit[];
  employment: Employment;
  startDate: string;
  rank?: string;
}

export interface DeactivateResult {
  success?: boolean;
  converted?: "membership.end";
  converted_from?: "user.deactivate";
  ended_now?: boolean;
}

export const accountOps = {
  createWithMembership(i: CreateWithMembershipInput) {
    return runAdminOp<{ uid: string }>("user.create_with_membership", {
      login_id: i.loginId, name: i.name, password: i.password,
      tenant: i.tenant, unit_roles: unitRolesObject(i.units), employment: i.employment,
      start_at: i.startDate,
      ...(i.rank ? { rank: i.rank } : {}),
      ...(i.personalEmail ? { personal_email: i.personalEmail } : {}),
    });
  },
  /** 가입 승인(user.approve_signup) — 소속 문서·claims·users 를 함수가 한 번에(실패 시 되돌림) */
  approveSignup(i: ApproveSignupInput) {
    return runAdminOp<{ uid: string; tenant: TenantKey; role: string }>("user.approve_signup", {
      targetUid: i.uid, tenant: i.tenant, unit_roles: unitRolesObject(i.units), employment: i.employment,
      start_at: i.startDate,
      ...(i.rank ? { rank: i.rank } : {}),
    });
  },
  /**
   * 가입 거부(user.reject_signup) — 가입 대기자만, 계정 삭제. tenant = 법인 관리자 경로의 자기 법인(rules 가 params.tenant 로 확인),
   * 전체 admin 은 생략 가능.
   */
  rejectSignup(uid: string, tenant?: TenantKey) {
    return runAdminOp("user.reject_signup", { targetUid: uid, ...(tenant ? { tenant } : {}) });
  },
  /**
   * 정지. 법인 관리자가 겸직자를 정지하면 함수가 그 법인 소속 종료로 바꿔 처리하고
   * result.converted = "membership.end" 를 돌려준다(전체 admin 은 바뀌지 않음 — 그래도 같이 처리).
   */
  deactivate(uid: string) {
    return runAdminOp<DeactivateResult>("user.deactivate", { targetUid: uid });
  },
  reactivate(uid: string) {
    return runAdminOp("user.reactivate", { targetUid: uid });
  },
  remove(uid: string) {
    return runAdminOp("user.delete", { id: uid });
  },
  /** 기기 하나 — mode logout = 강제 로그아웃(기기 유지), release = 등록 해제(삭제) */
  revokeDevice(uid: string, deviceId: string, mode: "logout" | "release") {
    return runAdminOp("device.revoke", { uid, device_id: deviceId, mode });
  },
  autoLoginOff(uid: string, deviceId: string) {
    // 끄기만 있는 action — 켜기는 본인이 기기관리에서
    return runAdminOp("device.set_auto_login", { uid, device_id: deviceId });
  },
  /** 모든 기기 로그아웃(토큰 폐기 — 사람 단위 하드) */
  revokeAllSessions(uid: string) {
    return runAdminOp("session.revoke_all", { uid });
  },
};
